import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Anchor, Compass, Ship, Cpu, Boxes } from "lucide-react";
import MmpLayout from "./MmpLayout";

const HERO_IMG = "https://images.unsplash.com/photo-1519455953755-af066f52f1a6?auto=format&fit=crop&w=1600&q=85";

const CATEGORIES = [
  { icon: Anchor, title: "Engineering & Design", items: ["Marine Engineers", "Naval Architects"] },
  { icon: Compass, title: "Science & Research", items: ["Nautical Scientists", "Oceanographers", "Marine Geologists"] },
  { icon: Ship, title: "Management & Operations", items: ["Port / Maritime Management Professionals"] },
  { icon: Cpu, title: "Technology", items: ["Marine Electrotechnologists"] },
  { icon: Boxes, title: "Logistics", items: ["Maritime Logistics Specialists"] },
];

const TICKER_MSGS = [
  "Marine Engineer shortlisted — Lagos — Just now",
  "Naval Architect deployed — Mombasa — 2 minutes ago",
  "Oceanographer contacted — Cape Town — 5 minutes ago",
  "Port Management Professional reviewed — Accra — 8 minutes ago",
  "Marine Electrotechnologist submitted — Abidjan — 12 minutes ago",
  "Maritime Logistics Specialist recommended — Dakar — 15 minutes ago",
];

export default function MmpLanding() {
  const [msgIdx, setMsgIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setMsgIdx((i) => (i + 1) % TICKER_MSGS.length), 3200);
    return () => clearInterval(t);
  }, []);

  return (
    <MmpLayout>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-40" style={{ background: "radial-gradient(1200px 500px at 20% 0%, rgba(14,165,233,0.12), transparent)" }} />
        <div className="mia-container relative py-14 lg:py-20">
          <div className="text-[0.62rem] font-bold tracking-[0.28em] uppercase text-sky-400">Marine & Maritime Professionals · MMP Portal</div>
          <h1 className="font-display font-black text-3xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05] tracking-tight max-w-4xl">
            The workforce deployment platform<br />
            for the <span className="text-sky-400">marine and maritime industry.</span>
          </h1>
          <p className="text-slate-400 max-w-2xl mt-5 leading-relaxed">
            Submit your CV and portfolio to get discovered for marine infrastructure projects, vessels, ports,
            offshore operations, and maritime opportunities across Africa and beyond.
          </p>
        </div>
      </section>

      {/* SINGLE TOGGLE CARD */}
      <section className="mia-container -mt-4 pb-16">
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#0E1525] shadow-[0_20px_60px_rgba(0,0,0,0.4)]" data-testid="mmp-single-toggle">
          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr,1fr]">
            {/* Left: content */}
            <div className="p-8 sm:p-10 lg:p-12">
              <div className="text-[0.62rem] font-bold tracking-[0.24em] uppercase text-sky-400">Get Discovered</div>
              <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl mt-3 tracking-tight">
                Marine & Maritime Professionals — Submit CV and Portfolio
              </h2>
              <p className="text-sm text-slate-400 mt-4 leading-relaxed">
                Marine Engineers · Naval Architects · Nautical Scientists · Oceanographers · Marine Geologists ·
                Port / Maritime Management · Marine Electrotechnologists · Maritime Logistics Specialists
              </p>
              <p className="text-slate-300 mt-5 leading-relaxed">
                Submit your CV and portfolio to get discovered for marine infrastructure projects, vessels, ports,
                offshore operations, and maritime opportunities.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/careers/submit"
                  className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-[#0B0F1A] font-bold text-sm uppercase tracking-[0.14em] px-6 py-3.5 rounded-lg transition-colors"
                  data-testid="mmp-submit-cta"
                >
                  Submit CV & Portfolio <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/careers/retrieve"
                  className="inline-flex items-center gap-2 border border-white/15 hover:border-sky-400 text-slate-200 font-semibold text-sm px-6 py-3.5 rounded-lg transition-colors"
                  data-testid="mmp-retrieve-cta"
                >
                  Retrieve / Update Profile
                </Link>
              </div>
            </div>
            {/* Right: image with soft mask */}
            <div className="relative min-h-[220px] lg:min-h-[440px] p-2 lg:p-3">
              <div
                className="w-full h-full rounded-xl bg-cover bg-center"
                style={{
                  backgroundImage: `url(${HERO_IMG})`,
                  WebkitMaskImage: "radial-gradient(ellipse at center, black 55%, transparent 100%)",
                  maskImage: "radial-gradient(ellipse at center, black 55%, transparent 100%)",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mia-container py-14">
        <div className="max-w-3xl">
          <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight">Marine & Maritime Professionals Across Key Disciplines</h3>
          <p className="text-slate-400 mt-3 leading-relaxed">
            Connecting marine infrastructure projects, vessels, ports, and offshore operators with verified maritime
            professionals across engineering, science, management, technology, and logistics.
          </p>
        </div>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          {CATEGORIES.map((c) => (
            <div key={c.title} className="rounded-xl bg-[#0E1525] border border-white/10 p-5 hover:border-sky-500/50 transition-colors" data-testid={`mmp-cat-${c.title.toLowerCase().replace(/[^a-z]+/g,"-")}`}>
              <div className="w-10 h-10 rounded-lg bg-sky-500/10 text-sky-400 flex items-center justify-center">
                <c.icon className="w-5 h-5" />
              </div>
              <div className="text-[0.6rem] tracking-[0.2em] uppercase text-sky-400 font-bold mt-4">{c.title}</div>
              <ul className="text-sm text-slate-300 mt-2 space-y-1">
                {c.items.map((i) => <li key={i}>{i}</li>)}
              </ul>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-5">+ More marine and maritime professionals being onboarded.</p>
      </section>

      {/* LIVE TICKER */}
      <section className="mia-container pb-6">
        <div className="rounded-lg border border-white/10 bg-[#0E1525] px-4 py-3 flex items-center gap-3 overflow-hidden">
          <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0 animate-pulse" />
          <span className="text-[0.62rem] font-bold tracking-[0.2em] uppercase text-emerald-400 shrink-0">Live Activity</span>
          <div key={msgIdx} className="text-sm text-slate-300 truncate animate-fade-up">{TICKER_MSGS[msgIdx]}</div>
        </div>
      </section>
    </MmpLayout>
  );
}
